import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormHelperText from "@mui/material/FormHelperText";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import {
  GridActionsCellItem,
  type GridActionsColDef,
  GridCell,
  type GridCellParams,
  type GridColDef,
  type GridPreProcessEditCellProps,
  type GridRenderCellParams,
  type GridRenderEditCellParams,
  GridRow,
  type GridRowId,
  GridRowModes,
  type GridRowModesModel,
  type GridRowParams,
  type GridSortModel,
  type GridValidRowModel,
} from "@mui/x-data-grid";
import { type DataGridProProps, useGridApiRef } from "@mui/x-data-grid-pro";
import React, { lazy, useCallback, useEffect, useMemo, useState } from "react";
import type * as z from "zod";

import { isEmptyString } from "~/utilities/string";

import { ActionDialog } from "../action_dialog";
import { HeadingContainer } from "../heading_container";
import { PublicSvgIcon } from "../public_svg_icon";
import { DataGridScrollShadow } from "../scrollable_data_grid_container";
import { RenderEditCellWithTooltip } from "./EditInputCellWithTooltip";

declare module "@mui/x-data-grid" {
  interface RowPropsOverrides {
    rowTooltipGetter?: (row: GridValidRowModel) => string | undefined;
  }
}

type DataGridProComponent = <R extends GridValidRowModel>(
  props: DataGridProProps<R> & React.RefAttributes<HTMLDivElement>,
) => React.JSX.Element;

const DataGridPro = lazy(() =>
  import("@mui/x-data-grid-pro").then((m) => ({ default: m.DataGridPro })),
) as unknown as DataGridProComponent;

function DataGridCellWithTestId({
  colIndex,
  ...props
}: React.ComponentProps<typeof GridCell>) {
  return (
    <GridCell {...props} colIndex={colIndex} data-testid={`cell-${colIndex}`} />
  );
}

function DataGridRowWithTooltip({
  rowTooltipGetter,
  ...props
}: React.ComponentProps<typeof GridRow> & {
  rowTooltipGetter?: (row: GridValidRowModel) => string | undefined;
}) {
  const tooltip = rowTooltipGetter?.(props.row);
  const row = <GridRow {...props} />;
  if (!tooltip) {
    return row;
  }
  return <Tooltip title={tooltip}>{row}</Tooltip>;
}

// TODO Create an interface for validating rows. Passing `zodSchema` directly
// to the component results in unnecessary tight-coupling.
export type EditableDataGridColDef<R extends GridValidRowModel> =
  GridColDef<R> & {
    editPlaceholder?: string | ((row: R) => string);
    renderViewCell?: React.ComponentType<GridRenderCellParams<R>>;
  };
interface EditableDataGridProps<
  Row extends { id: string | number; isNew?: boolean } & GridValidRowModel,
> {
  columns: EditableDataGridColDef<Row>[];
  rows: Row[];
  onRowsChange: (rows: Row[]) => void;
  createEmptyRow: () => Row;
  loading?: boolean;
  canDelete?: boolean | ((row: Row) => boolean);
  canEdit?: boolean | ((row: Row) => boolean);
  heading: string;
  headingIconName: string;
  canAdd?: boolean;
  addButtonLabel: string;
  zodSchema?: z.ZodObject<z.core.$ZodLooseShape>;
  error?: string;
  onAdd?: (row: Row) => void;
  onSave?: (row: Row) => void;
  onDelete?: (row: Row) => void;
  initialState?: DataGridProProps<Row>["initialState"];
  noRowsLabel: string;
  deleteDialogHeading: string;
  getDeleteDialogMessage: (row: Row) => string;
  deleteDialogConfirmText: string;
  deleteDialogCancelText: string;
  getRowsToDelete?: (row: Row, allRows: Row[]) => Row[];
  dataGridTestId?: string;
  addButtonDataTestId?: string;
  getRowClassName?: (params: GridRowParams<Row>) => string;
  getRowTooltip?: (row: Row) => string | undefined;
  shouldConfirmDelete?: (row: Row) => boolean;
}

export function EditableDataGrid<
  Row extends { id: string | number; isNew?: boolean } & GridValidRowModel,
>({
  columns,
  rows,
  onRowsChange,
  createEmptyRow,
  loading,
  heading,
  headingIconName,
  addButtonLabel,
  zodSchema,
  canAdd = true,
  canDelete = true,
  canEdit = true,
  error,
  onAdd,
  onSave,
  onDelete,
  initialState = undefined,
  noRowsLabel,
  deleteDialogHeading,
  getDeleteDialogMessage,
  deleteDialogConfirmText,
  deleteDialogCancelText,
  getRowsToDelete,
  dataGridTestId,
  addButtonDataTestId,
  getRowClassName,
  getRowTooltip,
  shouldConfirmDelete,
}: EditableDataGridProps<Row>) {
  const [internalRowModesModel, setInternalRowModesModel] =
    useState<GridRowModesModel>({});
  const [sortModel, setSortModel] = useState<GridSortModel>(
    initialState?.sorting?.sortModel ?? [],
  );
  const [lastAddedRowId, setLastAddedRowId] = useState<GridRowId | null>(null);
  const [validationErrors, setValidationErrors] = useState<
    Record<string, Record<string, string>>
  >({});
  const [touchedFields, setTouchedFields] = useState<
    Record<string, Record<string, boolean>>
  >({});
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [rowToDelete, setRowToDelete] = useState<Row | null>(null);

  useEffect(() => {
    if (rows.length === 0) {
      setInternalRowModesModel({});
      setValidationErrors({});
      setTouchedFields({});
      setLastAddedRowId(null);
      setIsDeleteDialogOpen(false);
      setRowToDelete(null);
    }
  }, [rows.length]);

  const apiRef = useGridApiRef();

  const emitChange = useCallback(
    (newRows: Row[]) => {
      onRowsChange(newRows);
    },
    [onRowsChange],
  );

  const removeRow = useCallback(
    (row: Row) => {
      const id = row.id;
      const filteredRows = rows.filter((r) => r.id !== id);
      emitChange(filteredRows);
      setInternalRowModesModel((prevModel) => {
        const nextModel = { ...prevModel };
        delete nextModel[id];
        return nextModel;
      });
      setValidationErrors((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      setTouchedFields((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      onDelete?.(row);
    },
    [onDelete, rows, emitChange],
  );

  const rowModesModel = useMemo(() => {
    const currentRowIds = new Set(rows.map((row) => row.id));
    const cleanedModel: GridRowModesModel = {};

    Object.keys(internalRowModesModel).forEach((id) => {
      if (currentRowIds.has(id) || currentRowIds.has(Number(id))) {
        cleanedModel[id] = internalRowModesModel[id];
      }
    });
    return cleanedModel;
  }, [rows, internalRowModesModel]);

  const isAddingAndEditingDisabled = useMemo(() => {
    return Object.values(internalRowModesModel).some(
      (rowModel) => rowModel.mode === GridRowModes.Edit,
    );
  }, [internalRowModesModel]);

  const firstEditableColumn = useMemo(
    () => columns.find((col) => col.editable),
    [columns],
  );

  useEffect(() => {
    if (lastAddedRowId && apiRef.current && firstEditableColumn) {
      const focusCell = () => {
        try {
          const rowExists = apiRef.current?.getRow(lastAddedRowId);
          if (rowExists) {
            apiRef.current?.setCellFocus(
              lastAddedRowId,
              firstEditableColumn.field,
            );
            setLastAddedRowId(null);
          } else {
            requestAnimationFrame(focusCell);
          }
        } catch {
          setLastAddedRowId(null);
        }
      };

      requestAnimationFrame(focusCell);
    }
  }, [lastAddedRowId, firstEditableColumn, apiRef]);

  const handleAdd = useCallback(() => {
    if (isAddingAndEditingDisabled) {
      return;
    }

    const newRow = createEmptyRow();
    const newRowWithFlag = { ...newRow, isNew: true };

    const nextRows = [...rows, newRowWithFlag];
    const nextModes = {
      ...internalRowModesModel,
      [newRow.id]: { mode: GridRowModes.Edit, ignoreModifications: false },
    };
    setInternalRowModesModel(nextModes);
    setLastAddedRowId(newRow.id);
    emitChange(nextRows);
    onAdd?.(newRow);
  }, [
    createEmptyRow,
    onAdd,
    rows,
    internalRowModesModel,
    emitChange,
    isAddingAndEditingDisabled,
  ]);

  const handleDelete = useCallback(
    (id: string | number) => {
      const existingRow = rows.find((r) => r.id === id);
      if (!existingRow) {
        return;
      }

      if (existingRow.isNew) {
        removeRow(existingRow);
        return;
      }

      if (shouldConfirmDelete && !shouldConfirmDelete(existingRow)) {
        removeRow(existingRow);
        return;
      }

      setRowToDelete(existingRow);
      setIsDeleteDialogOpen(true);
    },
    [rows, removeRow, shouldConfirmDelete],
  );

  const markFieldAsTouched = useCallback((rowId: GridRowId, field: string) => {
    setTouchedFields((prev) => ({
      ...prev,
      [rowId]: {
        ...prev[rowId],
        [field]: true,
      },
    }));
  }, []);

  const validateRowFields = useCallback(
    (id: GridRowId): Record<string, string> => {
      const errors: Record<string, string> = {};
      if (!zodSchema) {
        return errors;
      }

      Object.keys(zodSchema.shape).forEach((field) => {
        const schema = zodSchema.shape[field];
        const result = schema.safeParse(
          apiRef.current?.getRowWithUpdatedValues(id, "")[field],
        );
        if (!result.success && result.error.issues.length > 0) {
          errors[field] = result.error.issues[0].message;
        }
      });

      const rowCandidate = apiRef.current?.getRowWithUpdatedValues(id, "");
      const fullResult = zodSchema.safeParse(rowCandidate);
      if (!fullResult.success) {
        for (const issue of fullResult.error.issues) {
          const pathField = (issue.path?.[0] as string) || undefined;
          if (pathField && !errors[pathField]) {
            errors[pathField] = issue.message;
          }
        }
      }

      return errors;
    },
    [zodSchema, apiRef],
  );

  const processRowUpdate = useCallback(
    (newRow: GridValidRowModel) => {
      const existingRow = rows.find((r) => r.id === newRow.id);
      if (!existingRow) {
        return newRow as Row;
      }

      const updatedRow = { ...newRow, isNew: false } as Row;
      const next = rows.map((r) => (r.id === updatedRow.id ? updatedRow : r));
      emitChange(next);
      onSave?.(updatedRow);
      return updatedRow;
    },
    [onSave, emitChange, rows],
  );

  const handleCancelClick = useCallback(
    (id: GridRowId) => {
      const editedRow = rows.find((row) => row.id === id);
      if (editedRow?.isNew) {
        setInternalRowModesModel((prev) => ({
          ...prev,
          [id]: { mode: GridRowModes.View, ignoreModifications: true },
        }));

        requestAnimationFrame(() => {
          handleDelete(id);
        });
      } else {
        setInternalRowModesModel((prev) => ({
          ...prev,
          [id]: { mode: GridRowModes.View, ignoreModifications: true },
        }));
      }
    },
    [rows, handleDelete],
  );

  const handleSaveClick = useCallback(
    (id: GridRowId) => {
      const fieldErrors = validateRowFields(id);
      if (Object.keys(fieldErrors).length > 0) {
        setValidationErrors((prev) => ({
          ...prev,
          [id]: fieldErrors,
        }));
        return;
      } else {
        setValidationErrors((prev) => {
          const next = { ...prev };
          delete next[id];
          return next;
        });
      }

      setInternalRowModesModel((prev) => ({
        ...prev,
        [id]: { mode: GridRowModes.View, ignoreModifications: false },
      }));
    },
    [validateRowFields],
  );

  // Bypasses DataGridPro's native Enter-to-commit flow (blocked by the mode
  // override below) and saves the same way the row's Save button does.
  const handleCellKeyDown = useCallback(
    (params: GridCellParams<Row>, event: React.KeyboardEvent<HTMLElement>) => {
      if (event.key !== "Enter") {
        return;
      }
      if (rowModesModel[params.id]?.mode !== GridRowModes.Edit) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      handleSaveClick(params.id);
    },
    [rowModesModel, handleSaveClick],
  );

  const handleEditClick = useCallback(
    (id: GridRowId) => {
      if (isAddingAndEditingDisabled) {
        return;
      }

      setInternalRowModesModel((prev) => ({
        ...prev,
        [id]: { mode: GridRowModes.Edit, ignoreModifications: false },
      }));
    },
    [isAddingAndEditingDisabled],
  );

  const actionColumn = useMemo<GridActionsColDef<Row, unknown, unknown>>(
    () => ({
      field: "__actions__",
      type: "actions",
      headerName: "Actions",
      width: 90,
      getActions: ({ row }: GridRowParams<Row>) => {
        const id = row.id;

        const isExistingRow = rows.some((r) => r.id === id);
        if (!isExistingRow) {
          return [];
        }

        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;
        const actions = [];

        if (isInEditMode) {
          actions.push(
            <GridActionsCellItem
              key="save"
              icon={<PublicSvgIcon name="save" />}
              label="Save"
              onClick={() => handleSaveClick(id)}
              showInMenu={false}
              data-testid="row-save-button"
            />,
            <GridActionsCellItem
              key="cancel"
              icon={<PublicSvgIcon name="close" />}
              label="Cancel"
              onClick={() => handleCancelClick(id)}
              showInMenu={false}
              data-testid="row-cancel-button"
            />,
          );
        } else {
          const canEditRow =
            typeof canEdit === "function" ? canEdit(row) : canEdit;
          if (canEditRow) {
            actions.push(
              <GridActionsCellItem
                key="edit"
                icon={<PublicSvgIcon name="edit" />}
                label="Edit"
                onClick={() => handleEditClick(id)}
                showInMenu={false}
                disabled={isAddingAndEditingDisabled}
                data-testid="row-edit-button"
              />,
            );
          }

          const canDeleteRow =
            typeof canDelete === "function" ? canDelete(row) : canDelete;
          if (canDeleteRow) {
            actions.push(
              <GridActionsCellItem
                key="delete"
                icon={<PublicSvgIcon name="delete" />}
                label="Delete"
                onClick={() => handleDelete(id)}
                showInMenu={false}
                disabled={isAddingAndEditingDisabled}
                data-testid="row-delete-button"
              />,
            );
          }
        }

        return actions;
      },
    }),
    [
      rows,
      rowModesModel,
      canEdit,
      canDelete,
      handleCancelClick,
      handleDelete,
      handleEditClick,
      handleSaveClick,
      isAddingAndEditingDisabled,
    ],
  );

  const handleRowModesModelChange = useCallback(
    (newRowModesModel: GridRowModesModel) => {
      const validatedRowModesModel = { ...newRowModesModel };

      Object.keys(newRowModesModel).forEach((rowId) => {
        const currentModel = internalRowModesModel[rowId] ?? {
          mode: GridRowModes.View,
        };
        const newModel = newRowModesModel[rowId];
        if (
          currentModel?.mode === GridRowModes.View &&
          newModel?.mode === GridRowModes.Edit &&
          isAddingAndEditingDisabled
        ) {
          validatedRowModesModel[rowId] = currentModel;
          return;
        } else if (
          currentModel?.mode === GridRowModes.Edit &&
          newModel?.mode === GridRowModes.View
        ) {
          const fieldErrors = validateRowFields(rowId);
          if (Object.keys(fieldErrors).length > 0) {
            validatedRowModesModel[rowId] = currentModel;
            setValidationErrors((prev) => ({
              ...prev,
              [rowId]: fieldErrors,
            }));
            return;
          } else {
            setValidationErrors((prev) => {
              const next = { ...prev };
              delete next[rowId];
              return next;
            });
          }
          validatedRowModesModel[rowId].mode = GridRowModes.Edit;
        }
      });

      setInternalRowModesModel(validatedRowModesModel);
    },
    [internalRowModesModel, isAddingAndEditingDisabled, validateRowFields],
  );

  const handleSortModelChange = (newSortModel: GridSortModel) => {
    setSortModel(newSortModel);
  };

  const basePlusActions = useMemo<EditableDataGridColDef<Row>[]>(() => {
    // If canDelete/canEdit is a function, some rows may be deletable/editable so show the column.
    const hasDelete =
      typeof canDelete === "function"
        ? rows.some((r) => canDelete(r))
        : canDelete;
    const hasEdit =
      typeof canEdit === "function" ? rows.some((r) => canEdit(r)) : canEdit;

    return hasDelete || hasEdit ? [...columns, actionColumn] : columns;
  }, [columns, actionColumn, canDelete, canEdit, rows]);

  const validatedColumns = useMemo<EditableDataGridColDef<Row>[]>(() => {
    function attachZodValidation<R extends GridValidRowModel>(
      cols: EditableDataGridColDef<R>[],
      schemas: z.ZodObject<z.core.$ZodLooseShape> | undefined,
    ): EditableDataGridColDef<R>[] {
      if (!schemas) {
        return cols;
      }
      let editableColumnIndex = 0;
      return cols.map((c) => {
        if (!c.editable || !c.field) {
          return c;
        }
        const schema = schemas.shape[c.field];
        if (!schema || c.preProcessEditCellProps) {
          return c;
        }

        const currentEditableIndex = editableColumnIndex;
        editableColumnIndex++;

        const preProcessEditCellProps = (p: GridPreProcessEditCellProps) => {
          const rowId = p.id;
          const isExistingRow = rows.some((r) => r.id === rowId);
          if (!isExistingRow) {
            return { ...p.props, error: "" };
          }

          let validationError = "";
          if (p.hasChanged) {
            const isFieldTouched = touchedFields[rowId]?.[c.field!];

            const storedError = validationErrors[rowId]?.[c.field!];
            if (storedError) {
              setValidationErrors((prev) => {
                const next = { ...prev };
                if (next[rowId]) {
                  delete next[rowId][c.field!];
                  if (Object.keys(next[rowId]).length === 0) {
                    delete next[rowId];
                  }
                }
                return next;
              });
            }

            if (isFieldTouched) {
              const result = schema.safeParse(p.props.value);
              if (!result.success && result.error.issues.length > 0) {
                validationError = result.error.issues[0].message;
              }
            }
          }

          return { ...p.props, error: validationError };
        };

        const customRenderEditCell = (params: GridRenderEditCellParams) => {
          const rowId = params.id;
          const storedError = validationErrors[rowId]?.[c.field!];
          const cellError = storedError || params.error;

          const handleBlur = (event: React.FocusEvent) => {
            markFieldAsTouched(rowId, c.field!);
            params.onBlur?.(event);
          };

          const dataTest = `editable-cell-${c.field}-${rowId}`;
          const sharedProps = {
            ...params,
            error: cellError,
            dataTestId: dataTest,
            editableColumnIndex: currentEditableIndex,
            onBlur: handleBlur,
          };

          if (!c.renderEditCell) {
            return <RenderEditCellWithTooltip {...sharedProps} />;
          }

          const rendered = c.renderEditCell(sharedProps);
          if (!cellError) {
            return rendered;
          }

          const placement = currentEditableIndex % 2 === 1 ? "top" : "bottom";
          return (
            <Tooltip
              open
              title={cellError}
              color="error"
              placement={placement}
              arrow
            >
              <Box sx={{ width: "100%" }}>{rendered}</Box>
            </Tooltip>
          );
        };

        return {
          ...c,
          preProcessEditCellProps,
          renderEditCell: customRenderEditCell,
        };
      });
    }
    return attachZodValidation<Row>(basePlusActions, zodSchema);
  }, [
    basePlusActions,
    zodSchema,
    rows,
    validationErrors,
    touchedFields,
    markFieldAsTouched,
  ]);

  const columnsWithPlaceholders = useMemo<GridColDef<Row>[]>(() => {
    return validatedColumns.map((column: EditableDataGridColDef<Row>) => {
      let nextColumn: GridColDef<Row> = column;

      if (!column.editable && column.field) {
        const placeholderSpec = column.editPlaceholder;
        if (placeholderSpec) {
          const originalRenderCell = column.renderCell;
          nextColumn = {
            ...nextColumn,
            renderCell: (params) => {
              const isInEditMode =
                rowModesModel[String(params.id)]?.mode === GridRowModes.Edit;
              const v = params.value;
              const isEmpty = v === null || v === undefined || isEmptyString(v);

              let textToShow: string | undefined;
              if (typeof placeholderSpec === "function") {
                textToShow = placeholderSpec(params.row as Row);
              } else {
                textToShow = placeholderSpec;
              }

              if (isInEditMode && isEmpty && textToShow) {
                return (
                  <Box component="span" sx={{ color: "text.disabled" }}>
                    {textToShow}
                  </Box>
                );
              }

              if (originalRenderCell) {
                return originalRenderCell(params);
              }

              return params.value;
            },
          };
        }
      }

      const RenderViewCellComponent = column.renderViewCell;

      if (RenderViewCellComponent) {
        const baseRenderCell = nextColumn.renderCell;
        nextColumn = {
          ...nextColumn,
          renderCell: (params) => {
            const isInEditMode =
              rowModesModel[String(params.id)]?.mode === GridRowModes.Edit;
            if (isInEditMode) {
              if (baseRenderCell) {
                return baseRenderCell(params);
              }
              return params.value;
            }
            return <RenderViewCellComponent {...params} />;
          },
        };
      }

      const originalRenderHeader = nextColumn.renderHeader;
      nextColumn = {
        ...nextColumn,
        renderHeader: (params) => (
          <Box component="div" data-testid={`column-header-${column.field}`}>
            {originalRenderHeader
              ? originalRenderHeader(params)
              : (column.headerName ?? column.field)}
          </Box>
        ),
      };

      return nextColumn;
    });
  }, [validatedColumns, rowModesModel]);

  return (
    <Stack spacing={2.5}>
      <HeadingContainer
        title={
          <>
            <PublicSvgIcon name={headingIconName} /> {heading}
          </>
        }
        variant="h5"
        borderType="dashed"
      >
        {canAdd && (
          <Button
            variant="text"
            disabled={isAddingAndEditingDisabled}
            startIcon={<PublicSvgIcon name="add_filled" inheritColor />}
            onClick={handleAdd}
            {...(addButtonDataTestId && { "data-testid": addButtonDataTestId })}
          >
            {addButtonLabel}
          </Button>
        )}
      </HeadingContainer>
      <DataGridScrollShadow>
        <DataGridPro<Row>
          apiRef={apiRef}
          editMode="row"
          hideFooter
          disableColumnReorder
          disableColumnResize
          rows={rows}
          getRowId={(row: Row) => row.id}
          columns={columnsWithPlaceholders}
          className={`editable-grid${isAddingAndEditingDisabled ? " has-editing-row" : ""}`}
          processRowUpdate={processRowUpdate}
          onRowModesModelChange={handleRowModesModelChange}
          onCellKeyDown={handleCellKeyDown}
          loading={loading}
          rowModesModel={rowModesModel}
          sortModel={sortModel}
          initialState={initialState}
          onSortModelChange={handleSortModelChange}
          autoHeight
          localeText={{
            noRowsLabel,
          }}
          slots={{
            cell: DataGridCellWithTestId,
            ...(getRowTooltip && { row: DataGridRowWithTooltip }),
          }}
          slotProps={{
            ...(dataGridTestId && {
              root: { "data-testid": dataGridTestId },
            }),
            ...(getRowTooltip && {
              row: {
                // The grid only ever holds `Row` rows, so widening the slot's
                // row parameter to GridValidRowModel is safe (single upcast,
                // not a double `as unknown as`).
                rowTooltipGetter: getRowTooltip as (
                  row: GridValidRowModel,
                ) => string | undefined,
              },
            }),
          }}
          pinnedColumns={{ right: ["__actions__"] }}
          {...(getRowClassName && { getRowClassName })}
        />
      </DataGridScrollShadow>
      {error && <FormHelperText error>{error}</FormHelperText>}
      {isDeleteDialogOpen && rowToDelete && (
        <ActionDialog
          heading={deleteDialogHeading}
          confirmButtonMessage={deleteDialogConfirmText}
          cancelButtonMessage={deleteDialogCancelText}
          confirmButtonDataTestId="delete-confirm-button"
          cancelButtonDataTestId="delete-cancel-button"
          confirm={() => {
            const rowsToRemove = getRowsToDelete
              ? getRowsToDelete(rowToDelete, rows)
              : [rowToDelete];
            for (const row of rowsToRemove) {
              removeRow(row);
            }
            setIsDeleteDialogOpen(false);
            setRowToDelete(null);
          }}
          cancel={() => {
            setIsDeleteDialogOpen(false);
            setRowToDelete(null);
          }}
          message={getDeleteDialogMessage(rowToDelete)}
        />
      )}
    </Stack>
  );
}
